import { useState } from 'react';
import { Badge, Button, Group, Modal, Paper, SegmentedControl, Table, Text, TextInput, Tooltip } from '@mantine/core';
import { IconAlertTriangle, IconPlus } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { avisarError, Cabecera } from '../comun';
import { FormularioTercero, terceroVacio } from '../componentes/Terceros';
import type { Tercero } from '../tipos';

export function PaginaTerceros() {
  const qc = useQueryClient();
  const [tipo, setTipo] = useState('todos');
  const [q, setQ] = useState('');
  const [editar, setEditar] = useState<Tercero | 'nuevo' | null>(null);
  const lista = useQuery({ queryKey: ['terceros'], queryFn: () => api.get<Tercero[]>('/terceros') });
  const filas = (lista.data ?? []).filter(
    (t) =>
      (tipo === 'todos' || (tipo === 'cliente' ? t.esCliente : t.esProveedor)) &&
      (!q || `${t.nombre} ${t.nombreComercial ?? ''} ${t.identificacion ?? ''}`.toLowerCase().includes(q.toLowerCase())),
  );

  const archivar = async (t: Tercero) => {
    try {
      await api.post(`/terceros/${t.id}/archivar`, { archivado: true });
      await qc.invalidateQueries({ queryKey: ['terceros'] });
    } catch (e) {
      avisarError(e);
    }
  };

  return (
    <>
      <Cabecera titulo="Clientes y proveedores">
        <Button leftSection={<IconPlus size={16} />} onClick={() => setEditar('nuevo')}>
          Nuevo
        </Button>
      </Cabecera>
      <Paper withBorder p="sm" mb="md">
        <Group>
          <SegmentedControl
            value={tipo}
            onChange={setTipo}
            data={[
              { value: 'todos', label: 'Todos' },
              { value: 'cliente', label: 'Clientes' },
              { value: 'proveedor', label: 'Proveedores' },
            ]}
          />
          <TextInput placeholder="Buscar…" value={q} onChange={(e) => setQ(e.currentTarget.value)} style={{ flex: 1 }} />
        </Group>
      </Paper>
      <Paper withBorder>
        <Table.ScrollContainer minWidth={600}>
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Nombre</Table.Th>
                <Table.Th>Identificación</Table.Th>
                <Table.Th>Canarias</Table.Th>
                <Table.Th>Tipo</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filas.map((t) => (
                <Table.Tr key={t.id} style={{ cursor: 'pointer' }} onClick={() => setEditar(t)}>
                  <Table.Td>
                    <Text size="sm">{t.nombre}</Text>
                    {t.nombreComercial && (
                      <Text size="xs" c="dimmed">
                        {t.nombreComercial}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4}>
                      <Text size="sm">{t.identificacion ?? '—'}</Text>
                      {t.identificacionValida === false && (
                        <Tooltip label="Identificación dudosa: revísala">
                          <IconAlertTriangle size={14} color="var(--mantine-color-orange-6)" />
                        </Tooltip>
                      )}
                    </Group>
                  </Table.Td>
                  <Table.Td>{{ si: 'Sí', no: 'No', desconocido: '¿?' }[t.establecidoCanarias]}</Table.Td>
                  <Table.Td>
                    <Group gap={4}>
                      {t.esCliente && <Badge size="xs">Cliente</Badge>}
                      {t.esProveedor && (
                        <Badge size="xs" color="grape">
                          Proveedor
                        </Badge>
                      )}
                    </Group>
                  </Table.Td>
                  <Table.Td onClick={(e) => e.stopPropagation()}>
                    <Button size="xs" variant="subtle" color="gray" onClick={() => void archivar(t)}>
                      Archivar
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Paper>
      <Modal opened={editar !== null} onClose={() => setEditar(null)} title={editar === 'nuevo' ? 'Nuevo cliente o proveedor' : 'Editar'} size="lg">
        {editar && (
          <FormularioTercero
            inicial={editar === 'nuevo' ? terceroVacio('proveedor') : editar}
            onCancelar={() => setEditar(null)}
            onGuardado={() => setEditar(null)}
          />
        )}
      </Modal>
    </>
  );
}
